import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { teamQueryKeys } from 'TeamModule/query/teamQueryKeys';
import { getTeamTagsLookupApi, TeamTagsLookupResponse } from 'TeamModule/api/getTeamTagsLookupApi';
import { useCurrentContext } from 'hooks/useCurrentContext';
import ApiClientManager from 'core/apiClient';

export interface TeamTagOption {
  tag: string;
  count: number;
}

interface UseTeamTagsLookupQueryArgs {
  enabled?: boolean;
  /** Override the URL/auth-default tenant — use when the consumer picks a target tenant inside a form. */
  tenant?: string;
  /** Override the URL/auth-default realm id. */
  realmId?: string;
}

const sortTagOptions = (tags: Record<string, number>): TeamTagOption[] =>
  Object.entries(tags)
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));

export const useTeamTagsLookupQuery = ({
  enabled = true,
  tenant,
  realmId,
}: UseTeamTagsLookupQueryArgs = {}) => {
  const urlContext = useCurrentContext();
  const effectiveTenant = tenant ?? urlContext.tenant;
  const effectiveRealmId = realmId ?? urlContext.realmId;

  const { data, ...query } = useAppQuery<{
    options: TeamTagOption[];
    total: number;
  }>({
    queryKey: teamQueryKeys.list({
      identifier: `useTeamTagsLookupQuery:${effectiveTenant ?? ''}:${effectiveRealmId ?? ''}`,
    }),
    queryFn: async ({ token, buildConfig }) => {
      const config =
        tenant || realmId
          ? {
              headers: ApiClientManager.buildUrlHeaders({
                tenantName: effectiveTenant,
                realmId: effectiveRealmId,
              }),
            }
          : buildConfig();
      const response = await getTeamTagsLookupApi({
        params: undefined,
        token,
        config,
      });
      const body = response.data as TeamTagsLookupResponse;
      return {
        options: sortTagOptions(body?.tags ?? {}),
        total: body?.total ?? 0,
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });

  return {
    tagOptions: data?.options ?? [],
    totalTags: data?.total ?? 0,
    ...query,
  };
};

export default useTeamTagsLookupQuery;
